import * as React from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import { Avatar, FormControl, InputLabel, Select, MenuItem, CircularProgress, Breadcrumbs, Link, Stack } from '@mui/material';
import { Organization } from '../api/Endpoints/Organization';
import { useState, useEffect, useCallback } from 'react';
import { useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { queryClient } from '../config/queryClient';
import { setDefaultCurrencySymbol } from '../utils/currency';
import SearchIcon from '@mui/icons-material/Search';
import { Tooltip, Chip } from '@mui/material';
import GlobalSearch from '../components/GlobalSearch';
import NotificationsBell from '../components/NotificationsBell';

export default function ButtonAppBar(props) {
    const { onMobileNavOpen } = props;
    const location = useLocation();
    const navigate = useNavigate();
    const [organizations, setOrganizations] = useState([]);
    const [selectedOrganization, setSelectedOrganization] = useState('');
    const [loading, setLoading] = useState(false);
    const [organizationDetails, setOrganizationDetails] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);

    // Ctrl+Space to open search
    const handleGlobalKeyDown = useCallback((e) => {
        if ((e.ctrlKey || e.metaKey) && e.key === ' ') {
            e.preventDefault();
            setSearchOpen(true);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => window.removeEventListener('keydown', handleGlobalKeyDown);
    }, [handleGlobalKeyDown]);

    // Fetch organizations and initialize selected organization
    useEffect(() => {
        // Check if there's a previously selected organization in localStorage
        const storedOrganizationId = localStorage.getItem('selectedOrganizationId');
        if (storedOrganizationId) {
            setSelectedOrganization(storedOrganizationId);
            // Fetch details for the stored organization
            fetchOrganizationDetails(storedOrganizationId);
        }

        Organization.get({}).then((response) => {
            if (response.data && response.data.data && response.data.data.data) {
                setOrganizations(response.data.data.data);

                // If no organization is selected yet and we have organizations, select the first one
                if ((!storedOrganizationId || !response.data.data.data.find(org => org.id == storedOrganizationId)) && response.data.data.data.length > 0) {
                    const defaultOrgId = response.data.data.data[0].id;
                    setSelectedOrganization(defaultOrgId);
                    localStorage.setItem('selectedOrganizationId', defaultOrgId);

                    // Fetch details for the default organization
                    fetchOrganizationDetails(defaultOrgId);
                }
            }
        }).catch((error) => {
            console.error('Error fetching organizations:', error);
            toast.error('Failed to fetch organizations');
        });
    }, []);

    const fetchOrganizationDetails = (orgId) => {
        if (!orgId) return;

        setLoading(true);
        Organization.getById(orgId)
            .then((response) => {
                if (response.data && response.data.data && response.data.data.data) {
                    const orgData = response.data.data.data;
                    setOrganizationDetails(orgData);
                    // Cache default currency symbol for the organization
                    const symbol = orgData?.defaultTaxAccount?.default_currency?.symbol
                        || orgData?.default_currency?.symbol;
                    if (symbol) setDefaultCurrencySymbol(symbol);
                }
            })
            .catch((error) => {
                console.error('Error fetching organization details:', error);
                toast.error('Failed to fetch organization details');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleOrganizationChange = (event) => {
        const orgId = event.target.value;
        setSelectedOrganization(orgId);

        // Store the selected organization ID in localStorage for global access
        localStorage.setItem('selectedOrganizationId', orgId);

        // Dispatch a custom event to notify other parts of the app about the change
        window.dispatchEvent(new CustomEvent('organizationChange', {
            detail: { organizationId: orgId }
        }));

        // Trigger API call to fetch organization details
        fetchOrganizationDetails(orgId);

        // Clear all cached data and refetch active queries with the new org context
        queryClient.clear();
        queryClient.invalidateQueries();

        // Navigate to dashboard to ensure a clean state
        navigate('/dashboard/main');
        toast.success('Organization switched successfully');
    };

    return (<>
        <Box sx={{ flexGrow: 1, borderBottom: '1px solid #e1e3ea' }}>
            <AppBar className="headr-main" position="sticky" elevation={0} sx={{ backgroundColor: 'white', top: 0, zIndex: 1100 }}>
                <Toolbar>
                    <IconButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={onMobileNavOpen}
                        sx={{
                            mr: 2,
                            display: {
                                lg: 'none'
                            }
                        }}
                    >
                        <MenuIcon />
                    </IconButton>
                    <Box sx={{ flexGrow: 1, ml: 2 }}>
                        <Breadcrumbs
                            separator={<NavigateNextIcon fontSize="small" />}
                            aria-label="breadcrumb"
                        >
                            <Link
                                underline="hover"
                                color="inherit"
                                component={RouterLink}
                                to="/dashboard"
                                sx={{ fontSize: '14px', fontWeight: 500 }}
                            >
                                Dashboard
                            </Link>
                            {location.pathname
                                .split('/')
                                .filter((path) => path && path !== 'dashboard')
                                .map((path, index, array) => {
                                    const routeTo = `/dashboard/${array.slice(0, index + 1).join('/')}`;
                                    const isLast = index === array.length - 1;
                                    const title = path
                                        .replace(/-/g, ' ')
                                        .replace(/\b\w/g, (l) => l.toUpperCase());

                                    return isLast ? (
                                        <Typography
                                            key={path}
                                            color="textPrimary"
                                            sx={{ fontSize: '14px', fontWeight: 600 }}
                                        >
                                            {title}
                                        </Typography>
                                    ) : (
                                        <Link
                                            key={path}
                                            underline="hover"
                                            color="inherit"
                                            component={RouterLink}
                                            to={routeTo}
                                            sx={{ fontSize: '14px', fontWeight: 500 }}
                                        >
                                            {title}
                                        </Link>
                                    );
                                })}
                        </Breadcrumbs>
                    </Box>
                    <Stack direction="row" spacing={2} alignItems="center">
                        {/* Global Search */}
                        <Tooltip title="Search (Ctrl+Space)">
                            <IconButton
                                onClick={() => setSearchOpen(true)}
                                sx={{ color: 'text.secondary' }}
                            >
                                <SearchIcon />
                            </IconButton>
                        </Tooltip>
                        <NotificationsBell />
                        {/* Organization Dropdown */}
                        {organizations.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="organization-select-label">Org</InputLabel>
                                <Select
                                    labelId="organization-select-label"
                                    id="organization-select"
                                    value={selectedOrganization}
                                    label="Org"
                                    onChange={handleOrganizationChange}
                                    disabled={loading}
                                    sx={{
                                        color: 'black',
                                        '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0, 0, 0, 0.23)'
                                        },
                                        '&:hover .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0, 0, 0, 0.23)'
                                        },
                                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(0, 0, 0, 0.23)'
                                        }
                                    }}
                                >
                                    {loading ? (
                                        <MenuItem value="">
                                            <CircularProgress size={20} />
                                        </MenuItem>
                                    ) : (
                                        organizations.map((org) => (
                                            <MenuItem key={org.id} value={org.id}>
                                                {org.name || org.title || `Organization ${org.id}`}
                                            </MenuItem>
                                        ))
                                    )}
                                </Select>
                            </FormControl>
                        )}

                        <Avatar src={'https://www.spiderworks.in/theme/web/img/logo-sw.png'} sx={{ objectFit: 'cover' }} />
                    </Stack>
                </Toolbar>
            </AppBar>
        </Box >
        <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
    );
}